import { House } from '@phosphor-icons/react/dist/ssr/House';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { ChatsCircle } from '@phosphor-icons/react/dist/ssr/ChatsCircle';
import { UserCircle } from '@phosphor-icons/react/dist/ssr/UserCircle';
import { QrCode } from '@phosphor-icons/react/dist/ssr/QrCode';
import { useState, useEffect } from 'react';
import { isPWA } from '../../../utils/installPrompt';

// Figma measures visible artwork at 26px; Phosphor icons include ~23% padding
// in their 256×256 viewBox, so 26px artwork ≈ 34px Phosphor size prop.
const ICON_SIZE = 34;
const PROFILE_PHOTO_SIZE = 32;

/**
 * BottomTabBar Component
 *
 * Floating pill navigation for Profile (5 tabs) and Anon (3 tabs) users.
 * Profile: Home | Chats | QR | Posts | Profile
 * Anon:    Home | QR | Chats
 *
 * Icons only, no labels. QR button is oversized and extends above the pill.
 * Blue dot indicator floats above icons (top-center).
 */
const BottomTabBar = ({ currentScreen, userType, onQRPress, onNavigate }) => {
  // One 2s localStorage poll drives both nav indicators: the unread flag
  // (written by ProfileConnectionsListPage) and — Profile only — the avatar
  // photo, which otherwise showed stale after a mid-session photo change
  // (found on f/vcard-photo device pass). The raw-string guard means the
  // profile blob (occasionally a legacy base64 photo, so potentially large)
  // is re-parsed only when it actually changed.
  const [hasUnreadChats, setHasUnreadChats] = useState(() =>
    Boolean(localStorage.getItem('has_unread_chats'))
  );
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  useEffect(() => {
    let lastRaw = null;
    const poll = () => {
      setHasUnreadChats(Boolean(localStorage.getItem('has_unread_chats')));
      if (userType !== 'profile') return;
      const raw = localStorage.getItem('profile_user_data');
      if (raw === lastRaw) return;
      lastRaw = raw;
      try {
        setProfilePhotoUrl(raw ? JSON.parse(raw)?.photoUrl || null : null);
      } catch {
        // ignore parse errors
      }
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [userType]);

  const isActive = (screens) => screens.includes(currentScreen);

  // --- Tab definitions ---
  const profileTabs = [
    {
      key: 'home',
      screen: 'profile-connections-list',
      active: isActive(['profile-connections-list']),
      renderIcon: (active) => (
        <House size={ICON_SIZE} weight={active ? 'fill' : 'light'} color="#000" />
      ),
    },
    {
      key: 'chats',
      screen: 'profile-chat-list',
      active: isActive(['profile-chat-list']),
      badge: hasUnreadChats,
      renderIcon: (active) => (
        <PaperPlaneTilt size={ICON_SIZE} weight={active ? 'fill' : 'light'} color="#000" />
      ),
    },
    {
      key: 'qr',
      isCenter: true,
    },
    {
      key: 'posts',
      screen: 'profile-posts',
      active: isActive(['profile-posts']),
      renderIcon: (active) => (
        <ChatsCircle size={ICON_SIZE} weight={active ? 'fill' : 'light'} color="#000" />
      ),
    },
    {
      key: 'profile',
      screen: 'profile-settings',
      active: isActive(['profile-settings']),
      renderIcon: () =>
        profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt="Profile"
            style={{
              width: `${PROFILE_PHOTO_SIZE}px`,
              height: `${PROFILE_PHOTO_SIZE}px`,
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <UserCircle size={PROFILE_PHOTO_SIZE} weight="fill" color="#000" />
        ),
    },
  ];

  const anonTabs = [
    {
      key: 'home',
      screen: 'anon-connections-list',
      active: isActive(['anon-connections-list']),
      renderIcon: (active) => (
        <House size={ICON_SIZE} weight={active ? 'fill' : 'light'} color="#000" />
      ),
    },
    {
      key: 'qr',
      isCenter: true,
    },
    {
      key: 'chats',
      screen: 'anon-chat-list',
      active: isActive(['anon-chat-list']),
      renderIcon: (active) => (
        <PaperPlaneTilt size={ICON_SIZE} weight={active ? 'fill' : 'light'} color="#000" />
      ),
    },
  ];

  const tabs = userType === 'profile' ? profileTabs : anonTabs;

  // Pill width: 293px on 393px viewport = ~74.5% of screen width
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 90,
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: isPWA() ? 'calc(4px + env(safe-area-inset-bottom))' : '8px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          width: userType === 'profile' ? '74.5%' : '55%',
          maxWidth: '360px',
          backgroundColor: '#FFFFFF',
          borderRadius: '30px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          height: '47px',
          position: 'relative',
        }}
      >
        {tabs.map((tab) => {
          if (tab.isCenter) {
            // QR center button — extends above the pill
            return (
              <div
                key="qr"
                onClick={onQRPress}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  marginTop: '-10px',
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    backgroundColor: '#000000',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <QrCode size={38} weight="light" color="#FFFFFF" />
                </div>
              </div>
            );
          }

          // Regular tab — icon only, no label
          return (
            <div
              key={tab.key}
              onClick={() => onNavigate(tab.screen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                height: '100%',
              }}
            >
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                {tab.renderIcon(tab.active)}
                {tab.badge && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#007AFF',
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BottomTabBar;
